import React, {useEffect, useState} from "react";
import {ProductListFilterItemSelect} from "../../../typescript-library";
import {useProductListContext} from "../productListProvider/ProductListProvider";
import {useTranslation} from "react-i18next";
import {SelectOptionType} from "../../molecules/Select";
import {useMediaQuery} from "react-responsive";
import Checkbox from "../../lib/form/Checkbox/Checkbox";
import {RemoveFilterButton} from "../../atoms/RemoveFilterButton";

type ProductFilterSelectProps = {
  item: ProductListFilterItemSelect;
  onSelect?: (values: string[]) => void;
};

const defaultVisibleItems = 7;

const ProductFilterSelect: React.FC<ProductFilterSelectProps> = ({item, onSelect}) => {
  const {state, dispatch} = useProductListContext();
  const {t} = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const isMobile = useMediaQuery({query: "(max-width: 940px)"});
  const [open, setOpen] = useState(false);
  const [numOfVisibleItems, setNumOfVisibleItems] = useState(defaultVisibleItems);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [isOptionsSelectedWhileLoading, setIsOptionsSelectedWhileLoading] = useState(false);

  const fieldsThatNeedSorting = [
    "marke",
    "brand",
    "marque",
    "maerke",
    "merk",
    "hauptkategorie",
    "main-category",
    "categorie-principale",
    "hovedkategori",
    "hoofdcategorie",
    "kategorie",
    "category",
    "categorie",
    "kategori",
  ];

  useEffect(() => {
    if (state.filters[item.field]) {
      const filterValue = state.filters[item.field];
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        setSelectedOptions(new Set(filterValue));
      } else {
        setSelectedOptions(new Set());
      }
    } else {
      setSelectedOptions(new Set());
    }
  }, [state.trigger]);

  useEffect(() => {
    if (!state.loading) {
      setLocalLoading(false);
    }
  }, [state.loading, state.trigger]);

  const filterOptions: SelectOptionType[] = [];
  let rootValues = Array.from(item.rootValues.values());

  // options of certain fields are sorted alphabetically
  const isSortingRequired = fieldsThatNeedSorting.some((name) => item.field.toLocaleLowerCase().includes(name));
  if (isSortingRequired) {
    rootValues = rootValues.sort((a, b) => a.id.localeCompare(b.id));
  }

  rootValues.forEach((rootOption) => {
    let count: number;
    const possibleOption = Array.from(item.values.values()).find((possibleValue) => possibleValue.id === rootOption.id);

    if (possibleOption) {
      count = Number(possibleOption.count);
    } else {
      count = Number(rootOption.count);
    }
    if (count > 0 || selectedOptions.size) {
      filterOptions.push({
        value: rootOption.id,
        text: (
          <>
            <span>{String(rootOption.name)}</span>
            {count > 0 && (
              <>
                <span className="filter-item__count" aria-hidden>
                  {" (" + count + ")"}
                </span>
                <span className="visually-hidden">{`, ${count} ${t("product.items")}`}</span>
              </>
            )}
          </>
        ),
      });
    }
  });

  useEffect(() => {
    if (!localLoading) {
      if (isOptionsSelectedWhileLoading) {
        const oldValues = Array.from((state.filters[item.field] ?? []) as string[]).sort();
        const newValues = Array.from(selectedOptions).sort();
        if (oldValues.length === newValues.length && newValues.every((value, index) => value === oldValues[index])) {
          return;
        }
        if (!selectedOptions.size) {
          dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
        } else {
          dispatch({
            type: "SET_FILTER",
            payload: {field: item.field, value: Array.from(selectedOptions)},
          });
        }

        setIsOptionsSelectedWhileLoading(false);
        setLocalLoading(true);
      }
    }
  }, [localLoading]);

  const onChange = (value: string) => {
    const newSelectedOptions = new Set(selectedOptions);
    if (newSelectedOptions.has(value)) {
      newSelectedOptions.delete(value);
    } else {
      newSelectedOptions.add(value);
    }

    setSelectedOptions(newSelectedOptions);
    const values = Array.from(newSelectedOptions);

    if (onSelect) {
      onSelect(values);

      return;
    }

    if (localLoading) {
      setIsOptionsSelectedWhileLoading(true);

      return;
    }

    if (!values.length) {
      dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
    } else {
      dispatch({type: "SET_FILTER", payload: {field: item.field, value: values}});
    }

    setLocalLoading(true);
  };

  if (filterOptions.length < 2) {
    return null;
  }

  // Temporary i18n renaming of "Marke". Should be reverted until the
  // GSS cache is invalidated (should be 1 day after release and VP)
  let spanTitle = `${item.name || t(`product.${item.field}`)}`;
  switch (spanTitle) {
    case "product.marke":
      spanTitle = "Marke";
      break;
    case "product.brand":
      spanTitle = "Brand";
      break;
    case "product.marque":
      spanTitle = "Marque";
      break;
    case "product.maerke":
      spanTitle = "Mærke";
      break;
    case "product.merk":
      spanTitle = "Merk";
      break;
  }

  return (
    <div className="filter-item">
      <button
        className="filter-item__button"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setNumOfVisibleItems(defaultVisibleItems);
        }}
      >
        <span className="visually-hidden">{t("product.filterBy")}</span>
        <span>{spanTitle}</span>
        <i className={`icon icon-size--16 icon-chevron-${open ? "up" : "down"}`} />
      </button>
      {!!selectedOptions.size && isMobile && (
        <div className="filter-remove__wrapper">
          {Array.from(selectedOptions).map((option) => {
            return (
              <RemoveFilterButton
                key={option}
                onCancel={() => {
                  onChange(option);
                }}
              >
                {item.rootValues.get(option)?.name}
              </RemoveFilterButton>
            );
          })}
        </div>
      )}
      {open && (
        <div className="filter-item__content">
          {filterOptions.slice(0, numOfVisibleItems).map((option) => {
            return (
              <Checkbox
                key={option.value}
                wrapperClassName="filter-item__checkbox"
                className="filter-item__checkbox__inner"
                checked={selectedOptions.has(String(option.value))}
                color={"green"}
                onChange={() => {
                  onChange(String(option.value));
                }}
              >
                <div>{option.text}</div>
              </Checkbox>
            );
          })}
          {filterOptions.length > numOfVisibleItems && (
            <button
              className="filter-item__toggle-btn"
              onClick={() => {
                setNumOfVisibleItems(filterOptions.length);
              }}
            >
              {t("product.showAll")}
              <i className="icon icon-down--blue icon-size--16 ml-3" />
            </button>
          )}
          {filterOptions.length > defaultVisibleItems && numOfVisibleItems > defaultVisibleItems && (
            <button
              className="filter-item__toggle-btn"
              onClick={() => {
                setNumOfVisibleItems(defaultVisibleItems);
              }}
            >
              {t("product.showLess")}
              <i className="icon icon-top--blue icon-size--16 ml-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductFilterSelect;
